import React, { useMemo, ReactNode, useCallback } from "react";
import FlexBox from "../../../FlexBox/FlexBox";
import InnovationProductAdrienCachot, {
  IInnovationProductAdrienCachotProps,
} from "./InnovationProductAdrienCachot/InnovationProductAdrienCachot";
import {
  _LandingInnovationAdrienCachotProductList,
  _LandingInnovationAdrienCachotRoot,
} from "./LandingInnovationAdrienCachot.styled";
import ButtonContext from "../../../../Foundation/Button/Button.context";
import useBreakpoint from "../../../Grid/hooks/useBreakpoint";
import StyledDiv from "../../../StyledDiv/StyledDiv";
import { adrienCachotImages } from "../../../../Assets/images/adrien-cachot";
import MobileListAdrienCachot from "./MobileListAdrienCachot/MobileListAdrienCachot";
import DesktopListAdrienCachot from "./DesktopListAdrienCachot/DesktopListAdrienCachot";
import {
  InnovationVideoPlayingProvider,
  useInnovationDesktopVideoPlayingCount,
} from "./InnovationVideoPlayingContext";
import useOnScreen from "../../../../Hooks/useOnScreen";

export type ILandingInnovationProductAdrienCachot<T extends any = any> = {
  id_product: string;
  videoSlug: string;
  /** URL unique ou variantes alignées sur `SushishopVideo.customPosterImage` */
  poster?: string | { desktop?: string; mobile?: string };
  title: string;
  tag?: string;
  description?: ReactNode;
  price?: string;
  priceSubtitle?: string;
  ctaLabel?: string;
} & T & {};

interface ILandingInnovationAdrienCachotProps<T extends any = any> {
  products?: ILandingInnovationProductAdrienCachot<T>[];
  onClickProduct?: (product: ILandingInnovationProductAdrienCachot<T>) => void;
  showLessLabel: string;
  showMoreLabel: string;
  title?: string;
  renderProduct?: (
    data: IInnovationProductAdrienCachotProps<T>,
    index: number
  ) => ReactNode;
}

/** Calque rose : z-index bas pendant la vidéo desktop pour ne pas mélanger la vidéo (exclusion). */
const InnovationBlendLayerPink = () => {
  const videoPlayingCount = useInnovationDesktopVideoPlayingCount();
  const pinkBehindCards = videoPlayingCount > 0;

  return (
    <StyledDiv
      as="img"
      styles={{
        mixBlendMode: "exclusion",
        position: "absolute",
        top: "11%",
        width: "100%",
        aspectRatio: "180 / 101",
        zIndex: pinkBehindCards ? 0 : 3,
        pointerEvents: "none",
        transform: "translateX(3%)",
        mobile: {
          display: "none",
        },
      }}
      src={adrienCachotImages.bgPink}
      aria-hidden
    />
  );
};

const LandingInnovationAdrienCachot = <T extends any = any>(
  props: ILandingInnovationAdrienCachotProps<T>
) => {
  const {
    products,
    onClickProduct,
    showLessLabel,
    showMoreLabel,
    renderProduct,
    title,
  } = props;
  const { mobile } = useBreakpoint();
  /** Calques PNG lourds : montés quand la section entre dans le viewport (`ignoreReverse` : reste affiché au scroll) */
  const [containerRef, decorativeLayersVisible] = useOnScreen<HTMLElement>(
    { root: null, rootMargin: "160px 0px", threshold: 0 },
    { ignoreReverse: true }
  );

  const productList = useMemo(() => {
    return products?.slice(0, 6) || [];
  }, [products]);

  const itemRenderer = useCallback(
    (product: ILandingInnovationProductAdrienCachot<T>, index: number) => {
      const _data: IInnovationProductAdrienCachotProps = {
        className: "innovation-adrien-cachot-product-item",
        product: product,
        onClick: onClickProduct,
        showLessLabel: showLessLabel,
        showMoreLabel: showMoreLabel,
      };
      return (
        <React.Fragment key={product.id_product}>
          {renderProduct ? (
            renderProduct(_data, index)
          ) : (
            <InnovationProductAdrienCachot
              key={product.id_product}
              {..._data}
            />
          )}
        </React.Fragment>
      );
    },
    [onClickProduct, showLessLabel, showMoreLabel, renderProduct]
  );

  return (
    <ButtonContext.Provider
      value={{
        backgroundColor: "transparent",
        textColor: "white",
      }}
    >
      <_LandingInnovationAdrienCachotRoot
        className="landing-innovations-adrien-cachot"
        ref={containerRef}
      >
        {decorativeLayersVisible && (
          <StyledDiv
            as="img"
            styles={{
              mixBlendMode: "exclusion",
              position: "absolute",
              top: "-10%",
              left: 0,
              width: "100%",
              aspectRatio: "1 / 1",
              pointerEvents: "none",
              opacity: 0.5,
              mobile: {
                display: "none",
              },
            }}
            src={adrienCachotImages.bgBlue}
            aria-hidden
          />
        )}
        <InnovationVideoPlayingProvider>
          <div className="landing-innovations-adrien-cachot__container">
            <_LandingInnovationAdrienCachotProductList>
              <FlexBox
                justify="center"
                style={{
                  position: "relative",
                  maxWidth: 1348,
                  width: "100%",
                  margin: "0 auto",
                }}
              >
                {mobile ? (
                  // Mobile : stack sticky via GSAP ScrollTrigger
                  <MobileListAdrienCachot
                    title={title}
                    productList={productList}
                    itemRenderer={itemRenderer}
                    containerRef={containerRef}
                  />
                ) : (
                  // Desktop : même disposition que Umami (grid 2 colonnes, TouchScroll, hover)
                  <DesktopListAdrienCachot
                    title={title}
                    productList={productList}
                    itemRenderer={itemRenderer}
                  />
                )}
                {/* Rose : z-index réduit pendant la lecture vidéo desktop */}
                {decorativeLayersVisible && <InnovationBlendLayerPink />}
              </FlexBox>
            </_LandingInnovationAdrienCachotProductList>
          </div>
        </InnovationVideoPlayingProvider>
      </_LandingInnovationAdrienCachotRoot>
    </ButtonContext.Provider>
  );
};

export default LandingInnovationAdrienCachot;
